import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight, X, MapPin } from "lucide-react";
import { usePublicProjects } from "../hooks/useProjects";
import { SmartImage } from "./SmartImage";
import { Schema } from "./SEO";

const imgUrl = (g) => (typeof g === "string" ? g : g?.url);

export function collectServicePhotos(projects, serviceSlug, { city, limit = 12 } = {}) {
  const items = [];
  const list = projects.filter((p) => p.service_slug === serviceSlug && p.published !== false);
  const ordered = city ? [...list.filter((p) => p.city === city), ...list.filter((p) => p.city !== city)] : list;
  const perProject = ordered.map((p) => {
    const label = (p.category || "Project").toLowerCase();
    const raw = Array.isArray(p._gallery) ? p._gallery : p.gallery || [];
    return raw.map((g) => ({ g, p, label })).filter(({ g }) => imgUrl(g));
  });
  for (let i = 0; perProject.some((arr) => i < arr.length); i++) {
    for (const arr of perProject) {
      const it = arr[i];
      if (!it) continue;
      const { g, p, label } = it;
      items.push({
        url: imgUrl(g),
        alt: g?.alt || `${p.category} ${p.city} — ${p.title}`,
        caption: g?.alt || `${label.charAt(0).toUpperCase() + label.slice(1)} — ${p.city}`,
        city: p.city,
        project: p,
        hasAlt: Boolean(g?.alt),
      });
    }
  }
  return items.slice(0, limit);
}

function Lightbox({ items, index, onClose, onStep }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onStep(1);
      if (e.key === "ArrowLeft") onStep(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose, onStep]);
  const it = items[index];
  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true" data-testid="gallery-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white p-2" aria-label="Sluiten" data-testid="gallery-lightbox-close"><X size={24} /></button>
      <button onClick={(e) => { e.stopPropagation(); onStep(-1); }} className="absolute left-2 md:left-6 text-white/80 hover:text-white p-2" aria-label="Vorige" data-testid="gallery-lightbox-prev"><ChevronLeft size={32} /></button>
      <button onClick={(e) => { e.stopPropagation(); onStep(1); }} className="absolute right-2 md:right-6 text-white/80 hover:text-white p-2" aria-label="Volgende" data-testid="gallery-lightbox-next"><ChevronRight size={32} /></button>
      <figure className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
        <img src={it.url} alt={it.alt} className="w-full max-h-[80vh] object-contain" />
        <figcaption className="mt-3 flex items-center justify-between gap-4 text-sm text-white/80">
          <span data-testid="gallery-lightbox-caption">{it.caption}</span>
          <span className="text-white/50 text-xs">{index + 1} / {items.length}</span>
        </figcaption>
      </figure>
    </div>
  );
}

export function ServicePhotoGallery({ serviceSlug, serviceTitle, city, title, limit = 12 }) {
  const { allProjects } = usePublicProjects();
  const items = useMemo(() => collectServicePhotos(allProjects, serviceSlug, { city, limit }), [allProjects, serviceSlug, city, limit]);
  const [open, setOpen] = useState(null);
  if (items.length < 4) return null;

  const step = (d) => setOpen((i) => (i + d + items.length) % items.length);
  const schema = {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    name: title || `${serviceTitle} — gerealiseerde projecten`,
    image: items.map((it) => ({ "@type": "ImageObject", contentUrl: it.url, name: it.alt, description: it.caption, creditText: "Koning Aannemersbedrijf" })),
  };

  return (
    <section className="py-20 md:py-28 bg-[#0B0B0B] border-y border-koning-border" data-testid="service-photo-gallery">
      <Schema data={schema} id="image-gallery" />
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="eyebrow mb-3">Echte projecten, eigen team</div>
            <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight">{title || `Onze ${serviceTitle.toLowerCase()}s${city ? ` in en rond ${city}` : ""}`}</h2>
            <p className="mt-4 text-koning-muted max-w-2xl leading-relaxed">Geen stockfoto's: elke foto hieronder is door ons vaste team van loodgieter, elektricien en tegelzetter gerealiseerd. Klik op een foto voor een grotere weergave.</p>
          </div>
          <Link to="/projecten" className="btn-outline">Alle projecten <ArrowUpRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {items.map((it, i) => (
            <button
              key={it.url}
              type="button"
              onClick={() => setOpen(i)}
              className={`group relative overflow-hidden border border-koning-border hover:border-koning-gold/60 transition-colors text-left ${i % 7 === 0 ? "md:col-span-2 md:row-span-2 aspect-square" : "aspect-[4/5]"}`}
              data-testid={`gallery-photo-${i}`}
            >
              <SmartImage src={it.url} alt={it.alt} sizes="(max-width: 768px) 50vw, 25vw" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
              <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 bg-gradient-to-t from-black/85 to-transparent">
                <div className="text-white text-xs md:text-sm leading-snug line-clamp-2">{it.caption}</div>
                <div className="text-koning-gold text-[10px] tracking-[0.2em] uppercase mt-1 inline-flex items-center gap-1"><MapPin size={10} /> {it.city}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
      {open !== null && <Lightbox items={items} index={open} onClose={() => setOpen(null)} onStep={step} />}
    </section>
  );
}
